import { useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { cn } from "@/lib/utils";
import {
  AddressAutocomplete,
  type AddressDetails,
} from "@/components/owner/AddressAutocomplete";
import {
  Building,
  Building2,
  Check,
  HelpCircle,
  Home,
  LayoutPanelLeft,
  Upload,
} from "lucide-react";
import type { LucideIcon } from "lucide-react";
import type { PropertyTypeSlug, WizardState } from "@/lib/newPropertyWizard";

const PROPERTY_TYPES: { id: PropertyTypeSlug; label: string; icon: LucideIcon }[] = [
  { id: "house", label: "House", icon: Home },
  { id: "condo", label: "Condo", icon: Building2 },
  { id: "villa", label: "Villa", icon: Home },
  { id: "apartment", label: "Apartment", icon: Building },
  { id: "townhouse", label: "Townhouse", icon: Home },
  { id: "studio", label: "Studio", icon: LayoutPanelLeft },
  { id: "other", label: "Other", icon: HelpCircle },
];

interface Step1BasicsProps {
  state: WizardState;
  coverPhotoFile: File | null;
  onChange: (patch: Partial<WizardState>) => void;
  onCoverPhotoChange: (file: File | null) => void;
}

export function Step1Basics({ state, coverPhotoFile, onChange, onCoverPhotoChange }: Step1BasicsProps) {
  const fileRef = useRef<HTMLInputElement>(null);
  const previewUrl = coverPhotoFile
    ? URL.createObjectURL(coverPhotoFile)
    : state.coverPhotoUrl ?? null;

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Property basics</h1>
        <p className="text-muted-foreground text-sm mt-1">
          Name and location for your new property. You can refine details later.
        </p>
      </div>

      <div className="space-y-2">
        <Label htmlFor="property-name">Property name *</Label>
        <Input
          id="property-name"
          value={state.name}
          onChange={(e) => onChange({ name: e.target.value })}
          placeholder="e.g. Oceanview Condo"
          autoComplete="organization"
        />
      </div>

      <div className="space-y-2">
        <Label>Property type</Label>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          {PROPERTY_TYPES.map((type) => {
            const selected = state.propertyType === type.id;
            const Icon = type.icon;
            return (
              <button
                key={type.id}
                type="button"
                onClick={() => onChange({ propertyType: type.id })}
                className={cn(
                  "relative flex items-start gap-3 p-4 rounded-xl border-2 text-left transition-all",
                  selected
                    ? "border-primary bg-primary/5"
                    : "border-border hover:border-primary/40 hover:bg-muted/40"
                )}
              >
                {selected && <Check className="h-4 w-4 text-primary absolute top-3 right-3" />}
                <Icon
                  className={cn(
                    "h-5 w-5 shrink-0",
                    selected ? "text-primary" : "text-muted-foreground"
                  )}
                />
                <span className={cn("text-sm font-medium pr-6", selected && "text-primary")}>
                  {type.label}
                </span>
              </button>
            );
          })}
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="address">Address</Label>
        <AddressAutocomplete
          value={state.address}
          onChange={(val) => onChange({ address: val })}
          onSelect={(details: AddressDetails) => {
            onChange({
              address: details.street_address || details.formatted_address,
              city: details.city,
              state: details.state_province,
              country: details.country,
            });
          }}
          placeholder="Start typing your address..."
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="unit">Unit / Suite (optional)</Label>
        <Input
          id="unit"
          value={state.unit}
          onChange={(e) => onChange({ unit: e.target.value })}
          placeholder="e.g. 606, Suite 2B"
          autoComplete="address-line2"
        />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        <div className="space-y-2">
          <Label htmlFor="city">City</Label>
          <Input
            id="city"
            value={state.city}
            onChange={(e) => onChange({ city: e.target.value })}
            autoComplete="address-level2"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="state">State / Province</Label>
          <Input
            id="state"
            value={state.state}
            onChange={(e) => onChange({ state: e.target.value })}
            autoComplete="address-level1"
          />
        </div>
        <div className="space-y-2">
          <Label htmlFor="country">Country</Label>
          <Input
            id="country"
            value={state.country}
            onChange={(e) => onChange({ country: e.target.value })}
            autoComplete="country-name"
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label>Cover photo (optional)</Label>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const file = e.target.files?.[0] ?? null;
            onCoverPhotoChange(file);
          }}
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex items-center gap-3 w-full p-4 rounded-lg border border-dashed hover:bg-muted/50 transition-colors"
        >
          {previewUrl ? (
            <img src={previewUrl} alt="" className="h-14 w-14 rounded object-cover" />
          ) : (
            <div className="h-14 w-14 rounded bg-muted flex items-center justify-center">
              <Upload className="h-5 w-5 text-muted-foreground" />
            </div>
          )}
          <span className="text-sm text-muted-foreground">
            {coverPhotoFile || state.coverPhotoUrl ? "Change photo" : "Upload a cover photo"}
          </span>
        </button>
      </div>
    </div>
  );
}
